/**
 * specialtyResolver.js — Shared specialty id/name resolution
 * v206.1: Consolidates SPECIALTY_KEY_MAP duplicated across
 * CasesListScreen, DetectScreen, CaseHome, HomeScreen.
 *
 * Why this exists:
 * - Case data carries inconsistent specialty refs ('nose-sinus', 'Nose & Sinus',
 *   'Chronic Pain', 'wellness', etc) but the i18n keys and icon library use
 *   canonical short ids ('nose', 'pain', 'health-wellness').
 * - The fuzzy resolver maps any inbound form to the canonical id.
 *
 * Usage:
 *   import { resolveSpecialtyId, formatSpecialtyName } from '../utils/specialtyResolver'
 *   const canonicalId = resolveSpecialtyId(case.specialtyId || case.specialtyName)
 *   const displayName = formatSpecialtyName(case.specialtyId || case.specialtyName, t)
 */

// Canonical specialty ids — the keys used by i18n (specialties.*) and icon library.
// ★★★ ALL 26 SHIPPING SPECIALTIES ARE LISTED. The old list had eleven, and the
//   fifteen omissions were not inert: they fell through to a SUBSTRING match
//   (see resolveSpecialtyId step 3) where 'heart' matched 'ear'. Adding a
//   specialty to questionnaires.json without adding it here is the bug.
const CANONICAL_KEYS = [
  'ear', 'eye', 'nose', 'throat', 'head-neck',
  'stomach-gut', 'muscle-joints', 'skin', 'pain',
  'urinary', 'health-wellness', 'general',
  'heart', 'lungs', 'kidney', 'blood', 'womens-health',
  'brain-nerves', 'mental-health', 'vascular', 'infection',
  'sleep', 'feet', 'teeth', 'plastic-surgery', 'anesthesiology',
]

// Direct alias map — explicit mappings for human-facing strings the API may return
const ALIAS_MAP = {
  'ear': 'ear',
  'eye': 'eye',
  'nose': 'nose',
  'nose-sinus': 'nose',
  'nose & sinus': 'nose',
  'nose and sinus': 'nose',
  'throat': 'throat',
  'head-neck': 'head-neck',
  'head and neck': 'head-neck',
  'head & neck': 'head-neck',
  'head': 'head-neck',
  'stomach-gut': 'stomach-gut',
  'stomach & gut': 'stomach-gut',
  'stomach and gut': 'stomach-gut',
  'stomach': 'stomach-gut',
  'gut': 'stomach-gut',
  'muscle-joints': 'muscle-joints',
  'muscle & joints': 'muscle-joints',
  'muscle and joints': 'muscle-joints',
  'muscle': 'muscle-joints',
  'skin': 'skin',
  'pain': 'pain',
  'chronic pain': 'pain',
  'urinary': 'urinary',
  // catalog 3.7 (Dr. Rami 2026-10-11): the area id stays 'urinary'; its display name is now "Men's Health".
  "men's health": 'urinary',
  'mens health': 'urinary',
  'mens-health': 'urinary',
  "men's-health": 'urinary',
  'health-wellness': 'health-wellness',
  'health & wellness': 'health-wellness',
  'health and wellness': 'health-wellness',
  'wellness': 'health-wellness',
  'general': 'general',
  // ★ v242.87.0 — the display NAMES too, so a case carrying `specialtyName`
  //   ("Veins & Circulation") resolves as reliably as one carrying `specialtyId`.
  //   Before this, three specialties resolved to a key with no translation and
  //   silently title-cased, and one ("Heart") resolved to the WRONG specialty.
  'heart': 'heart',
  'lungs': 'lungs',
  'kidney': 'kidney',
  'blood': 'blood',
  'womens-health': 'womens-health',
  "women's health": 'womens-health',
  'womens health': 'womens-health',
  'brain-nerves': 'brain-nerves',
  'brain & nerves': 'brain-nerves',
  'brain and nerves': 'brain-nerves',
  'brain': 'brain-nerves',
  'mental-health': 'mental-health',
  'mental health': 'mental-health',
  'vascular': 'vascular',
  'veins & circulation': 'vascular',
  'veins and circulation': 'vascular',
  'veins': 'vascular',
  'infection': 'infection',
  'sleep': 'sleep',
  'feet': 'feet',
  'teeth': 'teeth',
  'plastic-surgery': 'plastic-surgery',
  'plastic surgery': 'plastic-surgery',
  'anesthesiology': 'anesthesiology',
  'wellness checks': 'health-wellness',
  'wellness-checks': 'health-wellness',
  'neck': 'head-neck',
  'chronic-pain': 'pain',
}

/**
 * Resolve any specialty id/name to its canonical short id.
 * Returns the input lowercased+normalized if no match (so unknowns still render).
 *
 * @param {string} input - id, name, or alias
 * @returns {string} canonical id (e.g. 'nose', 'head-neck') or normalized input
 */
export function resolveSpecialtyId(input) {
  if (!input) return ''
  const normalized = String(input).toLowerCase().trim()
  // 1. Exact alias hit
  if (ALIAS_MAP[normalized]) return ALIAS_MAP[normalized]
  // 2. Hyphenate spaces and try again ('Nose Sinus' -> 'nose-sinus')
  const hyphenated = normalized.replace(/\s+/g, '-').replace(/&/g, '').replace(/-+/g, '-')
  if (ALIAS_MAP[hyphenated]) return ALIAS_MAP[hyphenated]
  // 3. Exact match against canonical keys.
  //    ★★★ THIS WAS A SUBSTRING MATCH AND IT COST US EVERY HEART CASE.
  //    `hyphenated.includes(k)` with k='ear' matches 'heart', so a Heart case
  //    resolved to 'ear', rendered "Ears", and drew the ear icon — on eight
  //    screens. ViewSummary prints the raw name and therefore said "Heart",
  //    which is how the same case contradicted itself between two screens.
  //    Substring matching over a list of short ids has no safe form: 'ear' is
  //    inside 'heart', 'pain' is inside 'chronic-pain', 'skin' is a prefix of
  //    nothing today and of something tomorrow. ALIAS_MAP above is the place to
  //    add an inbound spelling; this step stays exact forever.
  //    Gated by scripts/check-specialty-resolver.mjs over all 26 specialties.
  const exact = CANONICAL_KEYS.find(k => hyphenated === k)
  if (exact) return exact
  return hyphenated
}

/**
 * Get the display name for a specialty, with safe fallbacks.
 * t() in this app returns the key string when missing (not undefined),
 * so we explicitly check for that.
 *
 * @param {string} input - id, name, or alias
 * @param {function} t - i18n translate function
 * @param {string} [rawNameFallback] - human name from case data, used if i18n key missing
 * @returns {string} display name
 */
export function formatSpecialtyName(input, t, rawNameFallback) {
  if (!input && !rawNameFallback) return t ? safeT(t, 'specialties.general', 'General') : 'General'
  const canonical = resolveSpecialtyId(input || rawNameFallback)
  if (t) {
    const i18nKey = `specialties.${canonical}`
    const translated = t(i18nKey)
    if (translated && translated !== i18nKey) return translated
  }
  // Fall back to provided raw name, or title-case the input
  if (rawNameFallback) return rawNameFallback
  return String(input).split(/[\s-]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
}

// Helper: t() that falls back when key is returned verbatim
function safeT(t, key, fallback) {
  const v = t(key)
  return (v && v !== key) ? v : fallback
}

// v224.3.1 → v224.8.0: Referral-aware specialty label.
//
// v224.8.0 change of intent: a referral-spawned case should read like any other
// case in the patient app — by the clinical CONCERN (body-system area), e.g.
// "Nose & Sinus", NOT the specialist type ("Otolaryngology"). The intake area is
// carried in specialtyId (the store derives it from cases.specialty_area_id), while
// cases.specialty holds the specialist.
//
// Rule:
//  - Built-out body-system area (specialtyId resolves to a canonical complaint
//    area like 'nose'/'ear'/'throat'): show the AREA name. The "Referred by Dr. X"
//    badge already conveys who/what specialist referred.
//  - General fallback (specialist with no built-out area, e.g. Cardiology → routed
//    through 'general') or missing area: show the SPECIALIST name, so the card
//    still reads sensibly (e.g. "Cardiology") rather than a bare "General".
//  - Patient-initiated cases: unchanged — resolve from specialtyId as before.
export function formatCaseSpecialty(caseObj, t) {
  if (!caseObj) return formatSpecialtyName(null, t)
  const docSpecialty = caseObj.specialtyName || caseObj.specialty?.name
  if (caseObj.originatorType === 'md_referral') {
    const areaId = resolveSpecialtyId(caseObj.specialtyId || caseObj.specialty?.id)
    // CANONICAL_KEYS are the built-out complaint areas; 'general' and unknowns
    // are intentionally excluded, so they cleanly fall through to the specialist.
    if (areaId && CANONICAL_KEYS.includes(areaId)) {
      return formatSpecialtyName(areaId, t)
    }
    return formatProviderSpecialty(docSpecialty, t)
  }
  return formatSpecialtyName(caseObj.specialtyId || caseObj.specialty?.id || docSpecialty, t, docSpecialty)
}

// v242.10.0 — Provider/doctor specialty label translation for patient-facing
// doctor labels ("Cardiology" → "Cardiología"). Unknown/free-text values pass
// through unchanged.
//
// ★★ v242.206.0 (TEST 1.34) — THIS RETURNS A STRING, ALWAYS. It used to hand its
//    input back on a translation miss, so the store's case shape
//    `specialty: { name }` came back as an OBJECT and React threw #31 inside the
//    doctor picker's specialty chip. An object with a string `.name` is formatted
//    by that name; anything else that is not a string is ''.
export function formatProviderSpecialty(specialty, t) {
  const name = typeof specialty === 'string' ? specialty
    : (specialty && typeof specialty === 'object' && typeof specialty.name === 'string') ? specialty.name
    : ''
  if (!name) return ''
  if (!t) return name
  const raw = name.trim()
  const key = 'providerSpecialty.' + raw
  const tr = t(key)
  return (tr && tr !== key) ? String(tr) : name
}

export { CANONICAL_KEYS }

/* ── Who may a PATIENT pick? ────────────────────────────────────────────────
 *
 * ★★ v242.158.0 — THIS PREDICATE HAD THREE IMPLEMENTATIONS AND NO OWNER, which
 *    is §1dl(c): HomeScreen's grid, StartCaseSheet's MD-scoped list, and (as of
 *    this version) the browse screen each spelled `s.patientSelectable !== false`
 *    inline. Three spellings of one rule is three places for it to drift when
 *    the admin panel gains a second way to hide an area. It lives here now.
 *
 * ★ `!== false` IS DELIBERATE AND IS NOT `=== true`. The field is optional in
 *   questionnaire data — most specialties simply omit it — so absence must mean
 *   selectable. Tightening this to a truthiness test would silently empty the
 *   patient's list the day someone hand-writes a new area without the flag.
 */
export function isPatientSelectable(specialty) {
  return !!specialty && specialty.patientSelectable !== false
}

// s100: the focus areas OFFERED for a new case. A retired one (patientSelectable:false, e.g.
// Health & Wellness > Sleep) stays in the catalog so existing cases still resolve.
export function selectableFocusAreas(specialty) {
  return ((specialty && specialty.focusAreas) || []).filter(isPatientSelectable)
}

// The areas the patient BROWSES. 'general' is excluded because it is promoted
// out of the list into its own card — it leads, it does not sit in the grid.
export function browsableSpecialties(specialties) {
  return (specialties || []).filter((s) => isPatientSelectable(s) && s.id !== 'general')
}
